import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
   Check,
   X,
   ArrowRight,
   Smartphone,
   Cloud,
   Shield,
   BarChart3,
   FileText,
   Users,
   Star,
   ChevronDown,
   TrendingUp,
   PlayCircle,
   CheckCircle,
   Sparkles,
   Lock,
   Rocket,
   Clock,
   MessageCircle,
   Phone,
   Bot,
   Workflow,
   Brain,
   Scan,
   Fingerprint,
   PieChart,
   LineChart,
   BarChart,
   Cctv,
   ShieldCheck,
   Send,
   Zap,
   Shirt,
   Scissors,
   ChevronLeft,
   ChevronRight,
} from 'lucide-react';
import { Image } from '@chakra-ui/react';
import Dashboard from '../../assets/img/dashboard.png';
import Navigation from './ZeronineHeader';
import Footer from './ZeronineFooter';
import { useDispatch, useSelector } from 'react-redux';
import { getPackageForWeb } from '../../redux/actions/package';
import { Link } from 'react-router-dom';

// AI Assistant Component with Free API Integration
const AIAssistant = ({ open, onClose }) => {
   const [messages, setMessages] = useState([
      {
         id: 1,
         text: "Hello! I'm your ZeroNine AI accounting assistant. I can help you with GST filing, invoice creation, financial reports, tax calculations, and any accounting questions you have!",
         isUser: false,
         timestamp: new Date(),
      },
   ]);
   const [inputText, setInputText] = useState('');
   const [isLoading, setIsLoading] = useState(false);
   const [voiceCommand, setVoiceCommand] = useState(false);
   const messagesEndRef = useRef(null);

   const scrollToBottom = () => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
   };

   useEffect(() => {
      scrollToBottom();
   }, [messages]);

   // Enhanced AI response system with better fallbacks
   const getAIResponse = async (userMessage) => {
      // Simulate API delay
      await new Promise((resolve) => setTimeout(resolve, 1000 + Math.random() * 500));

      const lowerMessage = userMessage.toLowerCase();

      // Comprehensive accounting knowledge base
      const responses = {
         // Purchase & Pricing Related
         buy: {
            response: `**How to Purchase ZeroNine:**\n\n🛒 **Purchase Process:**\n1. Visit our Pricing page\n2. Select your plan (Starter/Professional/Enterprise)\n3. Click "Start Free Trial"\n4. Complete business profile setup\n5. Choose payment method after trial\n\n💰 **Payment Options:**\n• Credit/Debit Cards (Visa, MasterCard, Rupay)\n• UPI Payments\n• Net Banking\n• Digital Wallets (Paytm, PhonePe, Google Pay)\n• Bank Transfer\n\n🎯 **Recommended Plan:**\nFor most businesses, we recommend the **Professional Plan (₹14,999/year)** which includes:\n• Unlimited invoices\n• Advanced AI features\n• Priority support\n• 50GB storage\n• Multi-user access\n\n*Start with 14-day FREE trial - no credit card required!*`,
            quickActions: ['View Pricing', 'Start Free Trial', 'Payment Methods'],
         },
         purchase: {
            response: `**Purchase ZeroNine - Easy Steps:**\n\n1. **Choose Plan**: Starter (₹4,999), Professional (₹14,999), or Enterprise (₹49,999)\n2. **Start Trial**: 14-day free trial with all features\n3. **Setup**: Complete your business profile with AI assistance\n4. **Payment**: Choose from multiple payment options\n\n💳 **Accepted Payment Methods:**\n✓ All major credit/debit cards\n✓ UPI (Google Pay, PhonePe, Paytm)\n✓ Net banking (All Indian banks)\n✓ Digital wallets\n✓ Bank transfers\n\n🔒 **Money-Back Guarantee:**\n30-day money-back guarantee if not satisfied.\n\n*Which plan are you interested in?*`,
            quickActions: ['Starter Plan', 'Professional Plan', 'Enterprise Plan'],
         },
         price: {
            response: `**ZeroNine Pricing Plans:**\n\n🚀 **STARTER** - ₹4,999/year\n• Basic GST invoicing\n• 5GB cloud storage\n• Email support\n• 100 invoices/month\n• Mobile app access\n\n⭐ **PROFESSIONAL** - ₹14,999/year *Most Popular*\n• Unlimited invoices\n• Advanced AI features\n• Priority support\n• 50GB storage\n• Multi-user access (5 users)\n• API access\n• Voice commands\n\n🏢 **ENTERPRISE** - ₹49,999/year\n• Everything unlimited\n• Dedicated account manager\n• White-label solution\n• Custom workflows\n• On-premise deployment\n• Unlimited users\n\n💫 **All plans include:**\n• 14-day FREE trial\n• No credit card required\n• GST compliance features\n• Automatic updates\n• Security features\n\n*Which plan fits your business needs?*`,
            quickActions: ['Starter Features', 'Professional Features', 'Compare Plans'],
         },
         package: {
            response: `**ZeroNine Package Details:**\n\nWe offer three comprehensive packages:\n\n📦 **Starter Package** - Perfect for solo entrepreneurs\n• GST invoicing & filing\n• Basic reports\n• 5GB storage\n• Email support\n\n📦 **Professional Package** - Best for growing businesses\n• All Starter features +\n• Advanced AI automation\n• Priority support\n• 50GB storage\n• Multi-user collaboration\n• API access\n\n📦 **Enterprise Package** - For large organizations\n• All Professional features +\n• Unlimited everything\n• Dedicated manager\n• Custom solutions\n• White-label option\n\n🎁 **Special Offer:** Start with 14-day free trial of any package!`,
            quickActions: ['Starter Package', 'Professional Package', 'Enterprise Package'],
         },

         // GST Related
         gst: {
            response: `**GST Filing with ZeroNine:**\n\n📋 **Automated GST Compliance**\n• Auto-calculation of CGST, SGST, IGST\n• GSTR-1, 2A, 2B, 3B, 9, 9C ready reports\n• Real-time GSTIN validation\n• HSN code auto-suggestion\n\n🔍 **Smart Reconciliation**\n• Automatic ITC (Input Tax Credit) matching\n• Error detection in GST returns\n• Supplier payment tracking\n• Missing invoice alerts\n\n⚡ **One-Click Filing**\n• Direct integration with GST portal\n• E-invoicing support\n• Bulk invoice uploading\n• Auto-fill GSTR forms\n\n📊 **GST Analytics**\n• Tax liability forecasting\n• Input credit optimization\n• Compliance calendar\n• Penalty risk alerts\n\n*Need help with specific GST form?*`,
            quickActions: ['GSTR-1 Filing', 'ITC Reconciliation', 'GST Due Dates'],
         },

         // Invoicing
         invoice: {
            response: `**Smart Invoicing Features:**\n\n🎯 **Quick Invoice Creation**\n• Create professional GST invoices in 30 seconds\n• 50+ customizable templates\n• Auto-fill customer GSTIN details\n• Recurring invoice automation\n• Bulk invoice generation\n\n📊 **Advanced Features**\n• Multi-currency support\n• Partial payments tracking\n• Payment reminder automation\n• Credit note generation\n• Discount management\n\n📱 **Access Anywhere**\n• Mobile app for on-the-go invoicing\n• Offline capability\n• Cloud backup & sync\n• Digital signature support\n\n🔔 **Automation**\n• Auto-payment reminders\n• Recurring invoice scheduling\n• Late payment interest calculation\n• Payment status tracking\n\n*Try: Invoices → Create New → Select template → Add items → Download/Share*`,
            quickActions: ['Create Invoice', 'Invoice Templates', 'Recurring Invoices'],
         },

         // Tax Management
         tax: {
            response: `**Complete Tax Management:**\n\n💰 **GST Management**\n• Auto tax calculation on transactions\n• GST return preparation & filing\n• Tax liability forecasting\n• Input tax credit optimization\n\n📈 **Income Tax**\n• Profit & loss computation\n• ITR preparation support\n• Tax saving suggestions\n• Advance tax calculations\n\n🏦 **TDS Management**\n• Automatic TDS deduction\n• TDS certificate generation (Form 16A)\n• Quarterly return preparation\n• TDS payment tracking\n\n🔒 **Tax Optimization**\n• Legal tax saving opportunities\n• Deduction eligibility checking\n• Compliance monitoring\n• Tax audit support\n\n📅 **Tax Calendar**\n• Never miss due dates\n• Auto-reminders for tax payments\n• Compliance status dashboard\n\n*Current feature: AI-powered tax saving recommendations*`,
            quickActions: ['GST Dashboard', 'Tax Saving Tips', 'TDS Management'],
         },

         // Reports & Analytics
         report: {
            response: `**Real-time Business Insights:**\n\n📊 **Financial Reports**\n• Profit & Loss statement\n• Balance Sheet\n• Cash Flow statement\n• Trial Balance\n• Fund flow statement\n\n📈 **Business Analytics**\n• Sales trend analysis\n• Expense categorization\n• Profit margin tracking\n• Customer payment behavior\n• Inventory turnover ratio\n\n📋 **Statutory Reports**\n• All GST returns (GSTR-1, 3B, 9, etc.)\n• TDS reports and challans\n• Income tax computations\n• Audit reports\n• MSME reports\n\n🤖 **AI-Powered Insights**\n• Predictive cash flow analysis\n• Revenue forecasting\n• Anomaly detection\n• Growth recommendations\n• Customer payment predictions\n\n🎯 **Custom Reports**\n• Drag-and-drop report builder\n• Custom metrics and KPIs\n• Scheduled report generation\n• Export to Excel/PDF\n\n*Pro tip: Set up automated weekly financial summaries*`,
            quickActions: ['P&L Report', 'GSTR Reports', 'Business Insights'],
         },

         // Features & Setup
         feature: {
            response: `**ZeroNine Key Features:**\n\n🤖 **AI-Powered Accounting**\n• Smart transaction categorization\n• Predictive analytics & forecasting\n• Voice commands for hands-free operation\n• Document intelligence with OCR\n• Automated reconciliation\n\n☁️ **Cloud-Based Platform**\n• Access from anywhere, any device\n• Automatic real-time backups\n• Multi-device synchronization\n• 99.9% uptime guarantee\n• Secure data centers\n\n🔒 **Bank-Level Security**\n• 256-bit end-to-end encryption\n• SOC 2 Type II compliance\n• Blockchain audit trail\n• Biometric login support\n• Regular security audits\n\n📱 **Mobile First Design**\n• iOS & Android native apps\n• Offline capability\n• Mobile document scanning\n• Push notifications\n• Touch/Face ID login\n\n👥 **Team Collaboration**\n• Role-based access control\n• Real-time team collaboration\n• Complete audit trails\n• Approval workflows\n• Task assignment & tracking\n\n🔄 **Automation & Integration**\n• 100+ pre-built workflows\n• API integration capability\n• Bank feed automation\n• Payment gateway integration\n• Third-party app connections\n\n*Want to know more about any specific feature?*`,
            quickActions: ['AI Features', 'Security', 'Mobile App', 'Integrations'],
         },

         // Setup & Migration
         setup: {
            response: `**Easy Setup & Migration:**\n\n🔄 **Data Migration**\n• Free data migration from any accounting software\n• Excel/CSV import with data validation\n• Tally data transfer\n• Automatic chart of accounts setup\n• Data integrity verification\n\n⚡ **Quick Setup**\n• 5-minute business profile setup\n• AI-powered configuration\n• Pre-built chart of accounts\n• Automatic GST setup based on business type\n• Customizable invoice templates\n\n🎓 **Training & Support**\n• Step-by-step video tutorials\n• Live training sessions\n• 24/7 chat support\n• Dedicated account manager (Pro+)\n• Comprehensive knowledge base\n\n📞 **Getting Started Guide**\n1. Sign up for free trial (no credit card)\n2. Complete business profile (5 minutes)\n3. Import existing data (optional)\n4. Set up GST details\n5. Create your first invoice\n6. Explore AI features\n\n🔧 **Ongoing Support**\n• Regular feature updates\n• GST law change alerts\n• Performance optimization\n• Custom feature requests (Enterprise)\n\n*Need help with migration from specific software?*`,
            quickActions: ['Data Migration', 'Video Tutorials', 'Contact Support'],
         },

         // Default response
         default: {
            response: `I'm here to help you with ZeroNine Accounting Software! 🌟\n\nI can assist you with:\n\n📋 **GST & Compliance**\n• GST filing process and due dates\n• Invoice requirements and templates\n• Return filing step-by-step guidance\n• Compliance alerts and updates\n\n💼 **Business Management**\n• Inventory tracking and management\n• Expense categorization and tracking\n• Customer and vendor management\n• Payment processing and tracking\n\n📊 **Reports & Analytics**\n• Financial statements generation\n• Business performance insights\n• Tax reports and computations\n• Custom report creation\n\n💰 **Pricing & Packages**\n• Plan comparison and recommendations\n• Feature breakdown by plan\n• Payment options and process\n• Free trial information\n\n🔧 **Technical Support**\n• Feature explanations and demos\n• Setup and configuration guidance\n• Troubleshooting assistance\n• Best practices and tips\n\nWhat specific area would you like help with today?`,
            quickActions: ['GST Help', 'Pricing Info', 'Feature Demo', 'Setup Guide'],
         },
      };

      // Intelligent keyword matching
      if (
         lowerMessage.includes('buy') ||
         lowerMessage.includes('purchase') ||
         lowerMessage.includes('order') ||
         lowerMessage.includes('payment') ||
         lowerMessage.includes('card') ||
         lowerMessage.includes('upi')
      ) {
         return responses.buy;
      }
      if (
         lowerMessage.includes('price') ||
         lowerMessage.includes('cost') ||
         lowerMessage.includes('plan') ||
         lowerMessage.includes('subscription') ||
         lowerMessage.includes('package')
      ) {
         return responses.price;
      }
      if (
         lowerMessage.includes('gst') ||
         lowerMessage.includes('return') ||
         lowerMessage.includes('filing') ||
         lowerMessage.includes('compliance')
      ) {
         return responses.gst;
      }
      if (lowerMessage.includes('invoice') || lowerMessage.includes('bill') || lowerMessage.includes('billing')) {
         return responses.invoice;
      }
      if (lowerMessage.includes('tax') || lowerMessage.includes('tds') || lowerMessage.includes('itr')) {
         return responses.tax;
      }
      if (
         lowerMessage.includes('report') ||
         lowerMessage.includes('analytics') ||
         lowerMessage.includes('dashboard') ||
         lowerMessage.includes('statement')
      ) {
         return responses.report;
      }
      if (
         lowerMessage.includes('feature') ||
         lowerMessage.includes('what can') ||
         lowerMessage.includes('how to') ||
         lowerMessage.includes('setup') ||
         lowerMessage.includes('migrat')
      ) {
         return responses.feature;
      }
      if (lowerMessage.includes('hello') || lowerMessage.includes('hi') || lowerMessage.includes('hey')) {
         return {
            response:
               "Hello! 👋 I'm your ZeroNine AI assistant. I can help you with GST compliance, invoicing, tax calculations, financial reports, pricing information, and all accounting needs. What would you like to know about our software?",
            quickActions: ['GST Features', 'Pricing Plans', 'How to Start'],
         };
      }

      return responses.default;
   };

   const handleSendMessage = async () => {
      if (!inputText.trim() || isLoading) return;

      const userMessage = {
         id: Date.now(),
         text: inputText,
         isUser: true,
         timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setInputText('');
      setIsLoading(true);

      try {
         const aiResponse = await getAIResponse(inputText);

         const botMessage = {
            id: Date.now() + 1,
            text: aiResponse.response,
            isUser: false,
            timestamp: new Date(),
            quickActions: aiResponse.quickActions,
         };

         setMessages((prev) => [...prev, botMessage]);
      } catch (error) {
         console.error('AI Error:', error);
         const errorMessage = {
            id: Date.now() + 1,
            text: "I apologize for the inconvenience. I'm currently experiencing high demand. Please try again in a moment or contact our support team at support@zeronine.com for immediate assistance.",
            isUser: false,
            timestamp: new Date(),
            quickActions: ['Try Again', 'Contact Support', 'View Documentation'],
         };
         setMessages((prev) => [...prev, errorMessage]);
      } finally {
         setIsLoading(false);
      }
   };

   const handleQuickAction = (action) => {
      setInputText(action);
      // Small delay to ensure state is updated
      setTimeout(() => {
         handleSendMessage();
      }, 100);
   };

   if (!open) return null;

   return (
      <div className="fixed bottom-6 right-6 z-50">
         <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 h-[600px] flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-4 rounded-t-2xl text-white">
               <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                     <Bot className="w-6 h-6" />
                     <span className="font-bold">ZeroNine AI Assistant</span>
                  </div>
                  <button onClick={onClose} className="text-white/80 hover:text-white transition-colors p-1">
                     <X className="w-5 h-5" />
                  </button>
               </div>
               <div className="text-xs text-blue-100 mt-1">
                  Ask me about accounting, GST, invoices, taxes, or reports
               </div>
            </div>

            {/* Chat Messages */}
            <div className="flex-1 p-4 overflow-y-auto bg-gray-50">
               <div className="space-y-4">
                  {messages.map((message) => (
                     <div key={message.id} className={`flex items-start gap-3 ${message.isUser ? 'justify-end' : ''}`}>
                        {!message.isUser && (
                           <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                              <Bot className="w-4 h-4 text-white" />
                           </div>
                        )}

                        <div
                           className={`rounded-2xl p-3 max-w-[80%] ${
                              message.isUser
                                 ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-tr-none'
                                 : 'bg-white border border-gray-200 rounded-tl-none shadow-sm'
                           }`}>
                           <p className="text-sm whitespace-pre-line">{message.text}</p>
                           <div className={`text-xs mt-2 ${message.isUser ? 'text-blue-100' : 'text-gray-500'}`}>
                              {message.timestamp.toLocaleTimeString([], {
                                 hour: '2-digit',
                                 minute: '2-digit',
                              })}
                           </div>

                           {/* Quick Actions */}
                           {!message.isUser && message.quickActions && (
                              <div className="flex flex-wrap gap-2 mt-3">
                                 {message.quickActions.map((action, index) => (
                                    <button
                                       key={index}
                                       onClick={() => handleQuickAction(action)}
                                       className="px-3 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-full text-xs font-medium transition-colors">
                                       {action}
                                    </button>
                                 ))}
                              </div>
                           )}
                        </div>

                        {message.isUser && (
                           <div className="w-8 h-8 bg-gradient-to-r from-gray-500 to-gray-600 rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-white text-xs font-bold">You</span>
                           </div>
                        )}
                     </div>
                  ))}

                  {isLoading && (
                     <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                           <Bot className="w-4 h-4 text-white" />
                        </div>
                        <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-none p-3 shadow-sm">
                           <div className="flex items-center gap-2 text-sm text-gray-600">
                              <div className="flex space-x-1">
                                 <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                                 <div
                                    className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                                    style={{ animationDelay: '0.2s' }}></div>
                                 <div
                                    className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                                    style={{ animationDelay: '0.4s' }}></div>
                              </div>
                              ZeroNine AI is thinking...
                           </div>
                        </div>
                     </div>
                  )}
                  <div ref={messagesEndRef} />
               </div>
            </div>

            {/* Input Area */}
            <div className="p-4 border-t border-gray-200 bg-white rounded-b-2xl">
               <div className="flex gap-2 mb-3">
                  <input
                     type="text"
                     value={inputText}
                     onChange={(e) => setInputText(e.target.value)}
                     onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                           e.preventDefault();
                           handleSendMessage();
                        }
                     }}
                     placeholder="Ask about accounting, GST, invoices..."
                     className="flex-1 px-4 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                     disabled={isLoading}
                  />
                  <button
                     onClick={handleSendMessage}
                     disabled={isLoading || !inputText.trim()}
                     className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-3 rounded-xl hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                     <Send className="w-4 h-4" />
                  </button>
               </div>

               {/* Quick Actions */}
               <div className="grid grid-cols-2 gap-2">
                  <button
                     onClick={() => handleQuickAction('How to buy ZeroNine package?')}
                     disabled={isLoading}
                     className="p-2 bg-blue-50 rounded-lg text-sm flex items-center justify-center gap-2 text-blue-700 hover:bg-blue-100 transition-all disabled:opacity-50">
                     <FileText className="w-4 h-4" />
                     Buy Package
                  </button>
                  <button
                     onClick={() => handleQuickAction('GST filing process')}
                     disabled={isLoading}
                     className="p-2 bg-green-50 rounded-lg text-sm flex items-center justify-center gap-2 text-green-700 hover:bg-green-100 transition-all disabled:opacity-50">
                     <Brain className="w-4 h-4" />
                     GST Help
                  </button>
                  <button
                     onClick={() => handleQuickAction('Create GST invoice')}
                     disabled={isLoading}
                     className="p-2 bg-purple-50 rounded-lg text-sm flex items-center justify-center gap-2 text-purple-700 hover:bg-purple-100 transition-all disabled:opacity-50">
                     <FileText className="w-4 h-4" />
                     Invoicing
                  </button>
                  <button
                     onClick={() => handleQuickAction('View pricing plans')}
                     disabled={isLoading}
                     className="p-2 bg-orange-50 rounded-lg text-sm flex items-center justify-center gap-2 text-orange-700 hover:bg-orange-100 transition-all disabled:opacity-50">
                     <BarChart3 className="w-4 h-4" />
                     Pricing
                  </button>
               </div>
            </div>
         </div>
      </div>
   );
};
export default function ZeroNineAccounting() {
   const dispatch = useDispatch();
   const [billingCycle, setBillingCycle] = useState('annual');
   const [scrolled, setScrolled] = useState(false);
   const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
   const [darkMode, setDarkMode] = useState(false);
   const [aiAssistantOpen, setAiAssistantOpen] = useState(false);
   const [activeFeatureTab, setActiveFeatureTab] = useState('ai');
   const [selectedPlan, setSelectedPlan] = useState(null);
   const [hoveredPlan, setHoveredPlan] = useState(null);
   const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
   const [activeSection, setActiveSection] = useState('home');

   const getCurrentPage = () => {
      const path = window.location.pathname;
      if (path.includes('/about')) return 'about';
      if (path.includes('/')) return 'home';
      return 'home';
   };

   const [currentPage, setCurrentPage] = useState(getCurrentPage());

   const PackageData = useSelector((state) => state.Package?.list || []);

   // Enhanced AI Features
   const [aiFeatures] = useState([
      {
         icon: Brain,
         title: 'AI-Powered Insights',
         description:
            'Get intelligent business recommendations and predictive analytics using machine learning algorithms',
         gradient: 'from-purple-500 to-pink-500',
         stats: '95% prediction accuracy',
         features: ['Predictive cash flow', 'Smart tax optimization', 'Revenue forecasting'],
      },
      {
         icon: Bot,
         title: 'Smart Automation',
         description: 'Automate repetitive tasks with intelligent workflows and RPA technology',
         gradient: 'from-blue-500 to-cyan-500',
         stats: 'Saves 20+ hours/month',
         features: ['Auto categorization', 'Invoice processing', 'Payment reminders'],
      },
      {
         icon: Scan,
         title: 'Document Intelligence',
         description: 'AI that reads and processes invoices, receipts, and documents with OCR technology',
         gradient: 'from-green-500 to-emerald-500',
         stats: '99.9% accuracy rate',
         features: ['Auto data extraction', 'Expense tracking', 'Document search'],
      },
      {
         icon: Workflow,
         title: 'Workflow Builder',
         description: 'Drag-and-drop interface to create custom automation workflows',
         gradient: 'from-orange-500 to-red-500',
         stats: 'No-code automation',
         features: ['Visual workflow editor', 'Conditional logic', 'Multi-step automation'],
      },
   ]);

   // Enhanced Security Features
   const [securityFeatures] = useState([
      {
         icon: Fingerprint,
         title: 'Biometric Authentication',
         description: 'Secure login with fingerprint and face recognition technology',
         gradient: 'from-red-500 to-orange-500',
         stats: 'Military-grade security',
         features: ['Face ID', 'Touch ID', 'Multi-factor auth'],
      },
      {
         icon: Shield,
         title: 'Blockchain Audit Trail',
         description: 'Immutable transaction records on distributed blockchain network',
         gradient: 'from-indigo-500 to-purple-500',
         stats: 'Tamper-proof records',
         features: ['Distributed ledger', 'Smart contracts', 'Transparent audit'],
      },
      {
         icon: Lock,
         title: 'Zero-Knowledge Encryption',
         description: 'Your data is encrypted end-to-end with zero-knowledge architecture',
         gradient: 'from-gray-500 to-blue-500',
         stats: 'Bank-level encryption',
         features: ['End-to-end encryption', 'Zero-knowledge proof', 'Client-side crypto'],
      },
      {
         icon: Cctv,
         title: 'Real-time Monitoring',
         description: '24/7 security monitoring with AI-powered threat detection',
         gradient: 'from-yellow-500 to-amber-500',
         stats: 'Instant threat alerts',
         features: ['Anomaly detection', 'Real-time alerts', 'Security dashboard'],
      },
   ]);

   // Advanced Analytics Features
   const [analyticsFeatures] = useState([
      {
         icon: LineChart,
         title: 'Real-time Analytics',
         description: 'Live business metrics and performance indicators with interactive dashboards',
         gradient: 'from-teal-500 to-cyan-500',
         stats: 'Live data streaming',
         features: ['Custom metrics', 'Real-time updates', 'Interactive charts'],
      },
      {
         icon: PieChart,
         title: 'Advanced Reporting',
         description: 'Deep insights with customizable reports and data visualization',
         gradient: 'from-violet-500 to-purple-500',
         stats: '50+ report templates',
         features: ['Custom reports', 'Data export', 'Scheduled reports'],
      },
      {
         icon: TrendingUp,
         title: 'Predictive Analytics',
         description: 'AI-driven forecasts and trend analysis for better decision making',
         gradient: 'from-rose-500 to-pink-500',
         stats: '90-day forecasts',
         features: ['Trend analysis', 'Seasonal patterns', 'Growth predictions'],
      },
   ]);

   const features = [
      {
         icon: FileText,
         title: 'Smart Invoicing',
         description: 'Create professional GST-compliant invoices instantly with AI-powered suggestions',
         gradient: 'from-blue-500 to-cyan-400',
         stats: '10K+ invoices created daily',
         advanced: true,
      },
      {
         icon: BarChart3,
         title: 'Real-time Analytics',
         description: 'Business insights at your fingertips with interactive dashboards and live data',
         gradient: 'from-purple-500 to-pink-400',
         stats: 'Live dashboard updates',
         advanced: true,
      },
      {
         icon: Cloud,
         title: 'Cloud-Based',
         description: 'Access anywhere, automatic backups with multi-cloud infrastructure',
         gradient: 'from-green-500 to-emerald-400',
         stats: '99.9% uptime guaranteed',
         advanced: true,
      },
      {
         icon: Shield,
         title: 'Bank-Level Security',
         description: '256-bit encryption protection with SOC 2 compliance',
         gradient: 'from-red-500 to-orange-400',
         stats: 'ISO certified security',
         advanced: true,
      },
      {
         icon: Smartphone,
         title: 'Mobile First',
         description: 'Powerful mobile app for iOS & Android with offline capabilities',
         gradient: 'from-indigo-500 to-blue-400',
         stats: '4.8★ app rating',
         advanced: true,
      },
      {
         icon: Users,
         title: 'Team Collaboration',
         description: 'Role-based access for your team with real-time collaboration',
         gradient: 'from-yellow-500 to-orange-400',
         stats: 'Unlimited team members',
         advanced: true,
      },
   ];

   const testimonials = [
      {
         name: 'Rajesh Kumar',
         role: 'CEO, Kumar Electronics',
         rating: 5,
         text: 'ZeroNine transformed our accounting! GST filing is now a breeze. The automation saved us 15+ hours weekly.',
         image: 'RK',
         company: 'Mumbai',
         revenue: '↑ 40% efficiency',
         aiUsage: 'Uses AI automation daily',
      },
      {
         name: 'Priya Sharma',
         role: 'Founder, Sharma Textiles',
         rating: 5,
         text: 'Best ROI of any software! Inventory management is flawless. Support team is incredibly responsive.',
         image: 'PS',
         company: 'Delhi',
         revenue: '↑ 3x growth',
         aiUsage: 'Loves predictive analytics',
      },
      {
         name: 'Amit Patel',
         role: 'Director, Patel Trading',
         rating: 5,
         text: 'Switched from expensive software to ZeroNine. Never looked back! Real-time insights are game-changing.',
         image: 'AP',
         company: 'Ahmedabad',
         revenue: '↓ 60% costs',
         aiUsage: 'Uses voice commands',
      },
   ];

   const faqs = [
      {
         q: 'Can I switch plans anytime?',
         a: 'Yes! Upgrade or downgrade anytime. Changes reflect immediately on your account.',
      },
      {
         q: 'Is my data secure?',
         a: 'Absolutely! We use bank-grade 256-bit encryption and are ISO 27001 certified with blockchain backup.',
      },
      {
         q: 'Do you offer migration support?',
         a: 'Yes! Our AI-powered migration tool and team will help you migrate all your data for free from any other platform.',
      },
      {
         q: 'What payment methods do you accept?',
         a: 'We accept all major credit/debit cards, UPI, net banking, digital wallets, and cryptocurrency.',
      },
      {
         q: 'Do you have AI features?',
         a: 'Yes! We offer advanced AI-powered insights, automation, document processing, and predictive analytics.',
      },
      {
         q: 'Can I use voice commands?',
         a: 'Absolutely! Our voice AI assistant understands natural language for creating invoices, reports, and more.',
      },
   ];

   const carouselRef = useRef(null);
   const [selectedBusinessType, setSelectedBusinessType] = useState('All');
   const [filteredPackages, setFilteredPackages] = useState([]);
   const activePackages = useMemo(() => {
      const uniquePackages = [];
      const seenNames = new Set();

      PackageData.filter((pkg) => pkg.status === true).forEach((pkg) => {
         if (!seenNames.has(pkg.name)) {
            seenNames.add(pkg.name);
            uniquePackages.push(pkg);
         }
      });

      return uniquePackages;
   }, [PackageData]);
   // Get available business types that actually have packages
   const getAvailableBusinessTypes = () => {
      const businessTypes = ['All', 'MOBILE', 'TEXTILE', 'EMBROIDERY', 'NORMAL'];
      return businessTypes.filter((businessType) => {
         if (businessType === 'All') return true;
         return activePackages.some((pkg) => pkg.businesstypename === businessType);
      });
   };

   useEffect(() => {
      let filtered = [];
      if (selectedBusinessType === 'All') {
         filtered = activePackages;
      } else {
         filtered = activePackages.filter((pkg) => pkg.businesstypename === selectedBusinessType);
      }

      setFilteredPackages(filtered);

      // Reset selected plan when filter changes - only select if there are packages
      if (filtered.length > 0) {
         setSelectedPlan(); // Select first package by default
      } else {
         setSelectedPlan(null); // No selection if no packages
      }
   }, [selectedBusinessType, activePackages]);

   // Handle plan selection
   const handlePlanSelect = (index) => {
      setSelectedPlan(index);
   };

   // Scroll functions
   const scrollPricing = (direction) => {
      if (carouselRef.current) {
         const scrollAmount = 400;
         carouselRef.current.scrollBy({
            left: direction === 'left' ? -scrollAmount : scrollAmount,
            behavior: 'smooth',
         });
      }
   };

   const scrollToPricingCard = (index) => {
      if (carouselRef.current && filteredPackages[index]) {
         const card = carouselRef.current.children[index];
         card.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center',
         });
         handlePlanSelect(index);
      }
   };

   useEffect(() => {
      const handleScroll = () => {
         setScrolled(window.scrollY > 50);
      };

      const handleMouseMove = (e) => {
         setMousePosition({ x: e.clientX, y: e.clientY });
      };

      window.addEventListener('scroll', handleScroll);
      window.addEventListener('mousemove', handleMouseMove);

      return () => {
         window.removeEventListener('scroll', handleScroll);
         window.removeEventListener('mousemove', handleMouseMove);
      };
   }, []);

   useEffect(() => {
      dispatch(getPackageForWeb());
   }, [dispatch]);

   const renderAIAssistant = () => <AIAssistant open={aiAssistantOpen} onClose={() => setAiAssistantOpen(false)} />;

   const renderCustomCursor = () => (
      <div
         className="fixed top-0 left-0 w-6 h-6 bg-blue-500 rounded-full mix-blend-difference pointer-events-none z-[100] transition-transform duration-100"
         style={{
            transform: `translate(${mousePosition.x - 12}px, ${mousePosition.y - 12}px)`,
         }}
      />
   );

   const renderAdvancedFeatures = () => (
      <section className="py-32 px-4 bg-gradient-to-br from-slate-50 to-blue-50">
         <div className="max-w-7xl mx-auto">
            <div className="text-center mb-20">
               <div className="inline-block px-4 py-2 bg-blue-100 rounded-full mb-4">
                  <span className="text-blue-600 font-bold">🚀 NEXT-GEN FEATURES</span>
               </div>
               <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-6">
                  Powered by Advanced
                  <span className="block bg-gradient-to-r pb-2 from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                     AI Technology
                  </span>
               </h2>
               <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Experience the future of accounting with cutting-edge artificial intelligence and machine learning
               </p>
            </div>

            {/* Feature Tabs */}
            <div className="flex justify-center mb-8">
               <div className="inline-flex bg-white/80 backdrop-blur-sm rounded-2xl p-2 border border-gray-200">
                  <button
                     onClick={() => setActiveFeatureTab('ai')}
                     className={`px-8 py-4 rounded-xl font-bold transition-all ${
                        activeFeatureTab === 'ai'
                           ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg'
                           : 'text-gray-700 hover:bg-gray-100'
                     }`}>
                     🤖 AI Features
                  </button>
                  <button
                     onClick={() => setActiveFeatureTab('security')}
                     className={`px-8 py-4 rounded-xl font-bold transition-all ${
                        activeFeatureTab === 'security'
                           ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg'
                           : 'text-gray-700 hover:bg-gray-100'
                     }`}>
                     🔒 Advanced Security
                  </button>
                  <button
                     onClick={() => setActiveFeatureTab('analytics')}
                     className={`px-8 py-4 rounded-xl font-bold transition-all ${
                        activeFeatureTab === 'analytics'
                           ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg'
                           : 'text-gray-700 hover:bg-gray-100'
                     }`}>
                     📊 Smart Analytics
                  </button>
               </div>
            </div>

            {/* AI Features Grid */}
            {activeFeatureTab === 'ai' && (
               <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                  {aiFeatures.map((feature, idx) => (
                     <div
                        key={idx}
                        className="group relative bg-white rounded-3xl p-8 border-2 border-gray-200 hover:border-transparent hover:shadow-2xl transition-all duration-500 overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-indigo-50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                        <div className="relative">
                           <div
                              className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                              <feature.icon className="w-10 h-10 text-white" />
                           </div>

                           <h3 className="text-2xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                           <p className="text-gray-600 mb-4 leading-relaxed">{feature.description}</p>

                           <div className="space-y-2 mb-4">
                              {feature.features.map((feat, i) => (
                                 <div key={i} className="flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4 text-green-500" />
                                    <span className="text-sm text-gray-600">{feat}</span>
                                 </div>
                              ))}
                           </div>

                           <div className="flex items-center gap-2 text-sm font-semibold text-blue-600">
                              <TrendingUp className="w-4 h-4" />
                              {feature.stats}
                           </div>
                        </div>
                     </div>
                  ))}
               </div>
            )}

            {/* Security Features Grid */}
            {activeFeatureTab === 'security' && (
               <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                  {securityFeatures.map((feature, idx) => (
                     <div
                        key={idx}
                        className="group relative bg-white rounded-3xl p-8 border-2 border-gray-200 hover:border-transparent hover:shadow-2xl transition-all duration-500 overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-red-50 to-orange-50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                        <div className="relative">
                           <div
                              className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                              <feature.icon className="w-10 h-10 text-white" />
                           </div>

                           <h3 className="text-2xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                           <p className="text-gray-600 mb-4 leading-relaxed">{feature.description}</p>

                           <div className="space-y-2 mb-4">
                              {feature.features.map((feat, i) => (
                                 <div key={i} className="flex items-center gap-2">
                                    <ShieldCheck className="w-4 h-4 text-green-500" />
                                    <span className="text-sm text-gray-600">{feat}</span>
                                 </div>
                              ))}
                           </div>

                           <div className="flex items-center gap-2 text-sm font-semibold text-red-600">
                              <Lock className="w-4 h-4" />
                              {feature.stats}
                           </div>
                        </div>
                     </div>
                  ))}
               </div>
            )}

            {/* Analytics Features Grid */}
            {activeFeatureTab === 'analytics' && (
               <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {analyticsFeatures.map((feature, idx) => (
                     <div
                        key={idx}
                        className="group relative bg-white rounded-3xl p-8 border-2 border-gray-200 hover:border-transparent hover:shadow-2xl transition-all duration-500 overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-green-50 to-teal-50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                        <div className="relative">
                           <div
                              className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                              <feature.icon className="w-10 h-10 text-white" />
                           </div>

                           <h3 className="text-2xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                           <p className="text-gray-600 mb-4 leading-relaxed">{feature.description}</p>

                           <div className="space-y-2 mb-4">
                              {feature.features.map((feat, i) => (
                                 <div key={i} className="flex items-center gap-2">
                                    <BarChart className="w-4 h-4 text-blue-500" />
                                    <span className="text-sm text-gray-600">{feat}</span>
                                 </div>
                              ))}
                           </div>

                           <div className="flex items-center gap-2 text-sm font-semibold text-green-600">
                              <TrendingUp className="w-4 h-4" />
                              {feature.stats}
                           </div>
                        </div>
                     </div>
                  ))}
               </div>
            )}
         </div>
      </section>
   );

   // Filter active packages and remove duplicates based on name

   // Get icon based on business type
   const getIcon = (businessTypeName) => {
      const iconMap = {
         MOBILE: Smartphone,
         TEXTILE: Shirt,
         NORMAL: Zap,
         EMBROIDERY: Scissors,
         DEFAULT: Zap,
      };
      return iconMap[businessTypeName] || iconMap.DEFAULT;
   };
   // Check if discount exists
   const hasDiscount = (pkg) => {
      return (pkg.discPer && pkg.discPer > 0) || (pkg.discAmt && pkg.discAmt > 0);
   };

   // const getPriceBasedOnDurType = (pkg) => {
   //    const durationType = pkg.durType;
   //    const amcPeriod = pkg.amcPeriod;

   //    if (billingCycle === 'monthly') {
   //       // Calculate monthly equivalent for comparison
   //       if (durationType && durationType.toLowerCase() === 'days') {
   //          if (amcPeriod && amcPeriod > 0) {
   //             // Convert daily price to monthly equivalent
   //             const dailyPrice = pkg.amcPrice / amcPeriod;
   //             return Math.round(dailyPrice * 30); // Approximate monthly
   //          }
   //       }

   //       // For monthly packages or no specific duration
   //       if (amcPeriod && amcPeriod > 0) {
   //          return Math.round(pkg.amcPrice / amcPeriod);
   //       }
   //       return Math.round(pkg.amcPrice / 12);
   //    } else {
   //       // Show actual package price (not converted)
   //       return pkg.amcPrice;
   //    }
   // };

   // Better approach - show actual duration without conversion:
   const getPriceBasedOnDurType = (pkg) => {
      // Always return the actual AMC price, let duration text handle the period
      return pkg.amcPrice;
   };

   // Calculate original price (before discount) - UPDATED
   const getOriginalPrice = (pkg) => {
      const basePrice = getPriceBasedOnDurType(pkg);

      if (pkg.discPer && pkg.discPer > 0) {
         return Math.round(basePrice / (1 - pkg.discPer / 100));
      }

      if (pkg.discAmt && pkg.discAmt > 0) {
         return Math.round(basePrice + pkg.discAmt);
      }

      return basePrice;
   };

   // Get final price (after discount) - UPDATED
   const getFinalPrice = (pkg) => {
      return getPriceBasedOnDurType(pkg);
   };

   const getDurationText = (pkg) => {
      const durationType = pkg.durType?.toLowerCase();
      const amcPeriod = pkg.amcPeriod;

      if (!durationType && !amcPeriod) {
         return billingCycle === 'monthly' ? '/month' : '/year';
      }

      // Handle days specifically
      if (durationType === 'days' || durationType === 'day' || durationType === 'daily') {
         if (amcPeriod === 1) return '/day';
         if (amcPeriod === 7) return '/week';
         if (amcPeriod === 15) return '/15 days';
         if (amcPeriod === 30) return '/month';
         return `/${amcPeriod} days`;
      }

      // Handle months
      if (durationType === 'months' || durationType === 'month' || durationType === 'monthly') {
         if (amcPeriod === 1) return '/month';
         if (amcPeriod === 3) return '/quarter';
         if (amcPeriod === 6) return '/half-year';
         if (amcPeriod === 12) return '/year';
         return `/${amcPeriod} months`;
      }

      // Handle years
      if (
         durationType === 'years' ||
         durationType === 'year' ||
         durationType === 'yearly' ||
         durationType === 'annual'
      ) {
         if (amcPeriod === 1) return '/year';
         return `/${amcPeriod} years`;
      }

      // Default case - show both type and period
      return `/${amcPeriod || ''} ${durationType || ''}`.trim();
   };

   // Get discount percentage text
   const getDiscountText = (pkg) => {
      if (pkg.discPer && pkg.discPer > 0) {
         return `${pkg.discPer}% OFF`;
      }
      if (pkg.discAmt && pkg.discAmt > 0) {
         const originalPrice = getOriginalPrice(pkg);
         const discountPercentage = Math.round((pkg.discAmt / originalPrice) * 100);
         return `${discountPercentage}% OFF`;
      }
      return null;
   };

   const scrollToSection = (section) => {
      setMobileMenuOpen(false);

      // If not on home page, navigate to home first
      if (currentPage !== 'home') {
         window.location.href = `/#${section}`;
         return;
      }

      // If on home page, scroll to section
      setActiveSection(section);
      const element = document.getElementById(section);
      if (element) {
         element.scrollIntoView({ behavior: 'smooth' });
      }
   };

   // Get discount amount
   const getDiscountAmount = (pkg) => {
      const originalPrice = getOriginalPrice(pkg);
      const finalPrice = getFinalPrice(pkg);
      return originalPrice - finalPrice;
   };

   const formatValue = (value) => {
      if (value === -100) return 'Unlimited';
      if (value === null || value === undefined) return '-';
      return typeof value === 'number' ? value.toLocaleString() : value;
   };

   // Helper function to format unlimited values
   const formatUnlimited = (value, suffix) => {
      if (value === -100) return `Unlimited ${suffix}`;
      return `${value.toLocaleString()} ${suffix}`;
   };

   // Generate features array based on package data
   const generateFeatures = (pkg) => {
      const features = [];

      // Basic features
      if (pkg.noOfUser) {
         features.push(formatUnlimited(pkg.noOfUser, 'Users')); // Remove { text: ... }
      }

      if (pkg.noOfCompanies) {
         features.push(formatUnlimited(pkg.noOfCompanies, 'Companies'));
      }

      if (pkg.noOfWp) {
         features.push(formatUnlimited(pkg.noOfWp, 'Daily WhatsApp'));
      }

      if (pkg.noOfEInvoice) {
         features.push(formatUnlimited(pkg.noOfEInvoice, 'E-Invoices'));
      }

      if (pkg.noOfEways) {
         features.push(formatUnlimited(pkg.noOfEways, 'E-Way Bills'));
      }

      // Feature flags - Remove all { text: ... } wrappers
      if (pkg.isCrm) {
         features.push('CRM System');
      }

      if (pkg.isToDo) {
         features.push('To-Do Management');
      }

      if (pkg.isMobileApp) {
         features.push('Mobile App Access');
      }

      if (pkg.isDashboardGst) {
         features.push('GST Dashboard');
      }

      if (pkg.is2stepVerify) {
         features.push('2-Step Verification');
      }

      if (pkg.is2a2b) {
         features.push('2A/2B Matching');
      }

      if (pkg.isDailyWp) {
         features.push('Daily Waybill Generation');
      }

      // New features from backend - Remove all { text: ... } wrappers
      if (pkg.sales_order) {
         features.push('Sales Order');
      }

      if (pkg.purchase_order) {
         features.push('Purchase Order');
      }

      if (pkg.sales_invoice) {
         features.push('Sales Invoice');
      }

      if (pkg.purchase_challan) {
         features.push('Purchase Challan');
      }

      if (pkg.purchase_invoice) {
         features.push('Purchase Invoice');
      }

      if (pkg.sales_return) {
         features.push('Sales Return');
      }

      if (pkg.purchase_return) {
         features.push('Purchase Return');
      }

      if (pkg.issue) {
         features.push('Issue Management');
      }

      if (pkg.receive) {
         features.push('Receive Management');
      }

      if (pkg.cash) {
         features.push('Cash Management');
      }

      if (pkg.bank) {
         features.push('Bank Management');
      }

      if (pkg.jv) {
         features.push('Journal Voucher');
      }

      if (pkg.stock_transfer) {
         features.push('Stock Transfer');
      }

      if (pkg.share_company) {
         features.push('Share Company');
      }

      if (pkg.company_wise_master) {
         features.push('Company-wise Master');
      }

      if (pkg.delete_unused_master) {
         features.push('Delete Unused Master');
      }

      if (pkg.aamne_saamne_sale_purchase) {
         features.push('Aamne Saamne Sale/Purchase');
      }

      if (pkg.multiple_eway_bill_generate) {
         features.push('Multiple E-way Bill');
      }

      if (pkg.print_barcode) {
         features.push('Print Barcode');
      }

      if (pkg.bank_import) {
         features.push('Bank Import');
      }

      if (pkg.bulk_update_suspense_account) {
         features.push('Bulk Update Suspense Account');
      }

      // Products included
      if (pkg.product && pkg.product.length > 0) {
         const productCount = pkg.product.length;
         features.push(`${productCount} Product Modules`);
      }

      // AMC Period
      if (pkg.amcPeriod) {
         features.push(`${pkg.amcPeriod} Months AMC`);
      }

      return features.slice(0, 8);
   };

   return (
      <>
         <div className={`min-h-screen bg-white transition-colors duration-300 ${darkMode ? 'dark' : ''}`}>
            {/* {renderCustomCursor()}   */}
            <Navigation />
            <>
               {/* Hero Section - Ultra Modern */}
               <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-950 via-blue-950 to-indigo-950 pt-20">
                  {/* Animated Background */}
                  <div className="absolute inset-0">
                     <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.3),transparent_50%)]"></div>
                     <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_60%,rgba(99,102,241,0.3),transparent_50%)]"></div>
                     <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_80%,rgba(168,85,247,0.2),transparent_50%)]"></div>

                     {/* Floating Elements */}
                     <div
                        className="absolute w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"
                        style={{
                           left: `${mousePosition.x / 50}px`,
                           top: `${mousePosition.y / 50}px`,
                           transition: 'all 0.3s ease-out',
                        }}></div>
                     <div
                        className="absolute w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"
                        style={{
                           right: `${mousePosition.x / 80}px`,
                           bottom: `${mousePosition.y / 80}px`,
                           transition: 'all 0.5s ease-out',
                        }}></div>

                     {/* Grid Pattern */}
                     <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:50px_50px]"></div>

                     {/* Animated Particles */}
                     <div className="absolute inset-0 opacity-30">
                        {[...Array(20)].map((_, i) => (
                           <div
                              key={i}
                              className="absolute w-2 h-2 bg-white rounded-full animate-pulse"
                              style={{
                                 left: `${Math.random() * 100}%`,
                                 top: `${Math.random() * 100}%`,
                                 animationDelay: `${Math.random() * 5}s`,
                              }}
                           />
                        ))}
                     </div>
                  </div>

                  <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-20">
                     {/* Floating Badge */}
                     <div className="inline-flex items-center gap-3 mb-8 px-6 py-3 bg-white/10 backdrop-blur-xl rounded-full border border-white/20 shadow-2xl animate-pulse">
                        <div className="flex -space-x-2">
                           <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 border-2 border-white"></div>
                           <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 border-2 border-white"></div>
                           <div className="w-8 h-8 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 border-2 border-white"></div>
                        </div>
                        <span className="text-white font-bold">500+ businesses trust us</span>
                        <Sparkles className="w-5 h-5 text-yellow-400" />
                     </div>

                     {/* Main Headline */}
                     <h1 className="text-6xl md:text-8xl font-black text-white mb-2 leading-tight">
                        The Future of
                        <span className="block bg-gradient-to-r pb-3 from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent">
                           Business Accounting
                        </span>
                     </h1>

                     <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed">
                        Say goodbye to complex spreadsheets. Manage invoices, GST, inventory, and payments with India's
                        most powerful AI-driven accounting platform.
                     </p>

                     {/* CTA Buttons */}
                     <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16">
                        <button className="group relative px-10 py-5 rounded-2xl font-bold text-lg overflow-hidden shadow-2xl hover:shadow-blue-500/50 transition-all">
                           <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600"></div>
                           <div className="absolute inset-0 bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 opacity-0 group-hover:opacity-100 transition-all"></div>
                           <button onClick={() => scrollToSection('pricing')}>
                              <span className="relative text-white flex items-center gap-3">
                                 <Rocket className="w-6 h-6 group-hover:rotate-12 transition-transform" />
                                 Start Free Trial
                                 <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform" />
                              </span>
                           </button>
                        </button>
                        <button
                           onClick={() => setAiAssistantOpen(true)}
                           className="group px-10 py-5 rounded-2xl font-bold text-lg bg-green-600 text-white hover:bg-green-700 transition-all flex items-center gap-3">
                           <Bot className="w-6 h-6 group-hover:scale-110 transition-transform" />
                           Talk to AI
                        </button>
                     </div>

                     {/* Trust Indicators */}
                     <div className="flex flex-wrap justify-center gap-8 text-white/80">
                        <div className="flex items-center gap-2">
                           <CheckCircle className="w-5 h-5 text-green-400" />
                           <span>No credit card required</span>
                        </div>
                        <div className="flex items-center gap-2">
                           <CheckCircle className="w-5 h-5 text-green-400" />
                           <span>Cancel anytime</span>
                        </div>
                        <div className="flex items-center gap-2">
                           <CheckCircle className="w-5 h-5 text-green-400" />
                           <span>AI-powered setup</span>
                        </div>
                        <div className="flex items-center gap-2">
                           <CheckCircle className="w-5 h-5 text-green-400" />
                           <span>Voice commands</span>
                        </div>
                     </div>

                     {/* Floating Dashboard Preview */}
                     <div className="mt-20 relative">
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent z-10"></div>
                        <div className="relative bg-white/5 backdrop-blur-sm rounded-3xl border border-white/10 p-4 shadow-2xl transform hover:scale-105 transition-all duration-500">
                           <div className="bg-gradient-to-br from-blue-900/50 to-purple-900/50 rounded-2xl aspect-video flex items-center justify-center">
                              <Image src={Dashboard} alt="dashboard" w={'full'} h={'full'} />
                           </div>
                        </div>
                     </div>
                  </div>

                  {/* Scroll Indicator */}
                  <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
                     <ChevronDown className="w-8 h-8 text-white/50" />
                  </div>
               </section>

               {/* Logo Cloud */}
               <section className="py-16 bg-white border-y border-gray-200">
                  <div className="max-w-7xl mx-auto px-4">
                     <p className="text-center text-gray-500 font-semibold mb-8">TRUSTED BY LEADING BUSINESSES</p>
                     <div className="flex flex-wrap justify-center items-center gap-12 opacity-50">
                        {['TechCorp', 'FinServe', 'RetailHub', 'ManufactPro', 'ServicePlus'].map((name, idx) => (
                           <div key={idx} className="text-2xl font-black text-gray-400">
                              {name}
                           </div>
                        ))}
                     </div>
                  </div>
               </section>

               {/* Advanced Features Section */}
               {renderAdvancedFeatures()}

               {/* Features - Bento Grid Style */}
               <section id="features" className="py-32 px-4 bg-gradient-to-b from-white to-gray-50">
                  <div className="max-w-7xl mx-auto">
                     <div className="text-center mb-14">
                        <div className="inline-block px-4 py-2 bg-blue-100 rounded-full mb-6">
                           <span className="text-blue-600 font-bold">✨ POWERFUL FEATURES</span>
                        </div>
                        <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-3">
                           Everything You Need,
                           <span className="block bg-gradient-to-r pb-2 from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                              Nothing You Don't
                           </span>
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                           Powerful features that help you manage your business better with AI assistance
                        </p>
                     </div>

                     <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {features.map((feature, idx) => (
                           <div
                              key={idx}
                              className="group relative bg-white rounded-3xl p-8 border-2 border-gray-200 hover:border-transparent hover:shadow-2xl transition-all duration-300 overflow-hidden">
                              <div
                                 className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-10 transition-opacity`}></div>

                              {feature.advanced && (
                                 <div className="absolute top-4 right-4">
                                    <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                                       <Sparkles className="w-3 h-3" />
                                       AI Powered
                                    </div>
                                 </div>
                              )}

                              <div className="relative">
                                 <div
                                    className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg`}>
                                    <feature.icon className="w-8 h-8 text-white" />
                                 </div>

                                 <h3 className="text-2xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                                 <p className="text-gray-600 mb-4 leading-relaxed">{feature.description}</p>

                                 <div className="flex items-center gap-2 text-sm font-semibold text-gray-500">
                                    <TrendingUp className="w-4 h-4" />
                                    {feature.stats}
                                 </div>
                              </div>
                           </div>
                        ))}
                     </div>
                  </div>
               </section>

               {/* Pricing - Interactive Comparison */}
               <section
                  id="pricing"
                  className="py-32 px-4 bg-gradient-to-br from-slate-950 via-blue-950 to-indigo-950 relative overflow-hidden">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(59,130,246,0.1),transparent_50%)]"></div>

                  <div className="relative max-w-7xl mx-auto">
                     <div className="text-center mb-8">
                        <div className="inline-block px-4 py-2 bg-white/10 backdrop-blur-xl rounded-full mb-6 border border-white/20">
                           <span className="text-white font-bold">💰 SIMPLE PRICING</span>
                        </div>
                        <h2 className="text-5xl md:text-6xl font-black text-white mb-3">Choose Your Perfect Plan</h2>
                        <p className="text-xl text-gray-300 mb-8">
                           Transparent pricing. No hidden fees. Cancel anytime.
                        </p>
                     </div>

                     {/* Business Type Filter */}
                     {getAvailableBusinessTypes().length > 1 && (
                        <div className="flex justify-center mb-10">
                           <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-2 border border-white/20">
                              <div className="flex flex-wrap gap-2 justify-center">
                                 {getAvailableBusinessTypes().map((businessType) => (
                                    <button
                                       key={businessType}
                                       onClick={() => setSelectedBusinessType(businessType)}
                                       className={`px-6 py-3 rounded-xl font-bold transition-all duration-300 ${
                                          selectedBusinessType === businessType
                                             ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg'
                                             : 'text-white/70 hover:text-white hover:bg-white/10'
                                       }`}>
                                       {businessType}
                                    </button>
                                 ))}
                              </div>
                           </div>
                        </div>
                     )}

                     <div className="relative mb-16">
                        {/* Navigation Arrows */}
                        {filteredPackages.length > 3 && (
                           <>
                              <button
                                 onClick={() => scrollPricing('left')}
                                 className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-4 z-10 bg-white/20 backdrop-blur-xl rounded-full p-3 border border-white/30 hover:bg-white/30 transition-all duration-300 hidden md:flex items-center justify-center">
                                 <ChevronLeft className="w-6 h-6 text-white" />
                              </button>
                              <button
                                 onClick={() => scrollPricing('right')}
                                 className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-4 z-10 bg-white/20 backdrop-blur-xl rounded-full p-3 border border-white/30 hover:bg-white/30 transition-all duration-300 hidden md:flex items-center justify-center">
                                 <ChevronRight className="w-6 h-6 text-white" />
                              </button>
                           </>
                        )}

                        {/* Carousel Container */}
                        <div
                           ref={carouselRef}
                           className="flex overflow-x-auto scrollbar-hide space-x-6 pb-8 px-8 md:px-0 snap-x snap-mandatory"
                           style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                           {filteredPackages.length > 0 ? (
                              filteredPackages.map((pkg, idx) => {
                                 const Icon = getIcon(pkg.businesstypename);
                                 const features = generateFeatures(pkg);
                                 const isSelectedOrHovered = selectedPlan === idx;
                                 const durationText = getDurationText(pkg);

                                 return (
                                    <div
                                       key={pkg.id}
                                       onClick={() => handlePlanSelect(idx)}
                                       onMouseEnter={() => setHoveredPlan(idx)}
                                       onMouseLeave={() => setHoveredPlan(null)}
                                       className={`
                  flex-none w-80 md:w-96 mt-8 rounded-3xl p-8 transition-all duration-300 cursor-pointer snap-center relative
                  ${
                     isSelectedOrHovered
                        ? 'bg-white shadow-2xl scale-105 border-4 border-blue-500'
                        : 'bg-white/5 backdrop-blur-xl border-2 border-white/10 hover:border-white/30'
                  }
                `}>
                                       {/* Selected/Hovered Badge */}
                                       {isSelectedOrHovered && (
                                          <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                                             <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg flex items-center gap-2">
                                                <Star className="w-4 h-4 fill-current" />
                                                {selectedPlan === idx ? 'Selected Plan' : ''}
                                             </div>
                                          </div>
                                       )}

                                       <div className="mb-6">
                                          <Icon
                                             className={`w-12 h-12 mb-4 ${
                                                isSelectedOrHovered ? 'text-blue-600' : 'text-white'
                                             }`}
                                          />
                                          <h3
                                             className={`text-3xl font-black mb-2 ${
                                                isSelectedOrHovered ? 'text-gray-900' : 'text-white'
                                             }`}>
                                             {pkg.name || `Plan ${idx + 1}`}
                                          </h3>
                                          <p
                                             className={`text-sm ${
                                                isSelectedOrHovered ? 'text-gray-600' : 'text-gray-400'
                                             }`}>
                                             {pkg.detail || 'Premium features for your business'}
                                          </p>
                                       </div>

                                       <div className="mb-8">
                                          {/* Original Price with Strike-through (if discount exists) */}
                                          {hasDiscount(pkg) && (
                                             <div className="flex items-center justify-center gap-2 mb-2">
                                                <span
                                                   className={`text-2xl font-bold line-through ${
                                                      isSelectedOrHovered ? 'text-gray-400' : 'text-white/40'
                                                   }`}>
                                                   ₹{getOriginalPrice(pkg).toLocaleString()}
                                                </span>
                                                <span
                                                   className={`text-sm font-bold px-2 py-1 rounded ${
                                                      isSelectedOrHovered
                                                         ? 'bg-green-100 text-green-600'
                                                         : 'bg-green-500/20 text-green-300'
                                                   }`}>
                                                   Save ₹{getDiscountAmount(pkg).toLocaleString()}
                                                </span>
                                             </div>
                                          )}

                                          {/* Final Price */}
                                          <div className="flex items-baseline gap-2 justify-center">
                                             <span
                                                className={`text-6xl font-black ${
                                                   isSelectedOrHovered ? 'text-gray-900' : 'text-white'
                                                }`}>
                                                ₹{getFinalPrice(pkg).toLocaleString()}
                                             </span>
                                             <span
                                                className={`text-xl font-bold ${
                                                   isSelectedOrHovered ? 'text-gray-600' : 'text-white/60'
                                                }`}>
                                                {getDurationText(pkg)}
                                             </span>
                                          </div>
                                       </div>

                                       {/* Features List */}
                                       <div className="mb-8 space-y-4">
                                          {features &&
                                             Array.isArray(features) &&
                                             features.slice(0, 5).map((feature, featureIdx) => (
                                                <div key={featureIdx} className="flex items-center gap-3">
                                                   <Check
                                                      className={`w-5 h-5 ${
                                                         isSelectedOrHovered ? 'text-green-600' : 'text-green-400'
                                                      }`}
                                                   />
                                                   <span
                                                      className={`text-sm ${
                                                         isSelectedOrHovered ? 'text-gray-700' : 'text-gray-300'
                                                      }`}>
                                                      {feature}
                                                   </span>
                                                </div>
                                             ))}
                                       </div>

                                       <Link to={`/auth/registration?package=${pkg.id}`}>
                                          <button
                                             className={`w-full py-4 rounded-2xl font-bold text-lg transition-all transform hover:scale-105 ${
                                                isSelectedOrHovered
                                                   ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-xl hover:shadow-2xl'
                                                   : 'bg-white/10 backdrop-blur-xl border-2 border-white/20 text-white hover:bg-white/20'
                                             }`}>
                                             Get Started
                                          </button>
                                       </Link>
                                    </div>
                                 );
                              })
                           ) : (
                              <div className="flex-none w-80 md:w-96 mt-8 rounded-3xl p-8 bg-white/5 backdrop-blur-xl border-2 border-white/10 text-center">
                                 <div className="text-white text-lg">
                                    No packages available for {selectedBusinessType}
                                 </div>
                              </div>
                           )}
                        </div>

                        {/* Dots Indicator */}
                        {filteredPackages.length > 1 && (
                           <div className="flex justify-center space-x-2 mt-6">
                              {filteredPackages.map((_, idx) => (
                                 <button
                                    key={idx}
                                    onClick={() => scrollToPricingCard(idx)}
                                    className={`w-3 h-3 rounded-full transition-all ${
                                       selectedPlan === idx ? 'bg-white scale-125' : 'bg-white/30'
                                    }`}
                                 />
                              ))}
                           </div>
                        )}
                     </div>

                     {/* Comparison Table - Show if there's at least 1 package */}
                     {filteredPackages.length >= 1 && (
                        <div className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 p-8 overflow-x-auto">
                           <h3 className="text-2xl font-bold text-white mb-6 text-center">
                              Detailed Feature Comparison
                           </h3>
                           <table className="w-full min-w-[800px]">
                              <thead>
                                 <tr className="border-b border-white/10">
                                    <th className="text-left py-4 text-white font-bold">Feature</th>
                                    {filteredPackages.map((pkg) => (
                                       <th key={pkg.id} className="text-center py-4 text-white font-bold">
                                          {pkg.name || 'Plan'}
                                       </th>
                                    ))}
                                 </tr>
                              </thead>
                              <tbody>
                                 {/* Basic Features */}
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Users</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          <span
                                             className={
                                                pkg.noOfUser === -100 ? 'text-green-400 font-semibold' : 'text-gray-300'
                                             }>
                                             {formatValue(pkg.noOfUser)}
                                          </span>
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Companies</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          <span
                                             className={
                                                pkg.noOfCompanies === -100
                                                   ? 'text-green-400 font-semibold'
                                                   : 'text-gray-300'
                                             }>
                                             {formatValue(pkg.noOfCompanies)}
                                          </span>
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Daily WhatsApp</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          <span
                                             className={
                                                pkg.noOfWp === -100 ? 'text-green-400 font-semibold' : 'text-gray-300'
                                             }>
                                             {formatValue(pkg.noOfWp)}
                                          </span>
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">E-Invoices</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          <span
                                             className={
                                                pkg.noOfEInvoice === -100
                                                   ? 'text-green-400 font-semibold'
                                                   : 'text-gray-300'
                                             }>
                                             {formatValue(pkg.noOfEInvoice)}
                                          </span>
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">E-Way Bills</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          <span
                                             className={
                                                pkg.noOfEways === -100
                                                   ? 'text-green-400 font-semibold'
                                                   : 'text-gray-300'
                                             }>
                                             {formatValue(pkg.noOfEways)}
                                          </span>
                                       </td>
                                    ))}
                                 </tr>

                                 {/* Feature Flags */}
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">CRM System</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.isCrm ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">To-Do Management</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.isToDo ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Mobile App Access</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.isMobileApp ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">GST Dashboard</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.isDashboardGst ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">2-Step Verification</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.is2stepVerify ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">2A/2B Matching</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.is2a2b ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>

                                 {/* Additional Features */}
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Sales Order</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.sales_order ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Purchase Order</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.purchase_order ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Sales Invoice</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.sales_invoice ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Purchase Challan</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.purchase_challan ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Purchase Invoice</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.purchase_invoice ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Sales Return</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.sales_return ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Purchase Return</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.purchase_return ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Issue Management</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.issue ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Receive Management</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.receive ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Cash Management</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.cash ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Bank Management</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.bank ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Journal Voucher</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.jv ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Stock Transfer</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.stock_transfer ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Share Company</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.share_company ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Company-wise Master</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.company_wise_master ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Delete Unused Master</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.delete_unused_master ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Aamne Saamne Sale/Purchase</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.aamne_saamne_sale_purchase ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Multiple E-way Bill</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.multiple_eway_bill_generate ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Print Barcode</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.print_barcode ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Bank Import</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.bank_import ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                                 <tr className="border-b border-white/10">
                                    <td className="py-4 text-gray-300 font-medium">Bulk Update Suspense Account</td>
                                    {filteredPackages.map((pkg) => (
                                       <td key={pkg.id} className="text-center py-4">
                                          {pkg.bulk_update_suspense_account ? (
                                             <Check className="w-5 h-5 text-green-400 mx-auto" />
                                          ) : (
                                             <X className="w-5 h-5 text-red-400 mx-auto" />
                                          )}
                                       </td>
                                    ))}
                                 </tr>
                              </tbody>
                           </table>
                        </div>
                     )}
                  </div>
               </section>

               {/* Testimonials - Modern Cards */}
               <section id="testimonials" className="py-32 px-4 bg-white">
                  <div className="max-w-7xl mx-auto">
                     <div className="text-center mb-20">
                        <div className="inline-block px-4 py-2 bg-yellow-100 rounded-full mb-6">
                           <span className="text-yellow-600 font-bold">⭐ CUSTOMER STORIES</span>
                        </div>
                        <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-6">Loved by 500+ Businesses</h2>
                        <p className="text-xl text-gray-600">Real results from real businesses using AI features</p>
                     </div>

                     <div className="grid md:grid-cols-3 gap-8">
                        {testimonials.map((testimonial, idx) => (
                           <div
                              key={idx}
                              className="group relative bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl p-8 border-2 border-blue-100 hover:border-blue-300 hover:shadow-2xl transition-all duration-300">
                              <div className="absolute top-6 right-6 flex gap-1">
                                 {[...Array(testimonial.rating)].map((_, i) => (
                                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                                 ))}
                              </div>

                              <div className="flex items-center gap-4 mb-6">
                                 <div className="relative">
                                    <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl blur opacity-50"></div>
                                    <div className="relative w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center text-white font-black text-xl shadow-lg">
                                       {testimonial.image}
                                    </div>
                                 </div>
                                 <div>
                                    <p className="font-black text-gray-900 text-lg">{testimonial.name}</p>
                                    <p className="text-sm text-gray-600 font-semibold">{testimonial.role}</p>
                                    <p className="text-xs text-gray-500">{testimonial.company}</p>
                                 </div>
                              </div>

                              <p className="text-gray-700 text-lg mb-6 leading-relaxed italic">"{testimonial.text}"</p>

                              <div className="space-y-3">
                                 <div className="pt-4 border-t border-blue-200">
                                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-green-100 rounded-full">
                                       <TrendingUp className="w-4 h-4 text-green-600" />
                                       <span className="text-sm font-bold text-green-600">{testimonial.revenue}</span>
                                    </div>
                                 </div>
                                 <div className="flex items-center gap-2 text-sm text-blue-600">
                                    <Bot className="w-4 h-4" />
                                    <span className="font-medium">{testimonial.aiUsage}</span>
                                 </div>
                              </div>
                           </div>
                        ))}
                     </div>

                     <div className="mt-16 text-center">
                        <div className="inline-flex items-center gap-3 px-6 py-3 bg-gray-100 rounded-full">
                           <div className="flex gap-1">
                              {[...Array(5)].map((_, i) => (
                                 <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                              ))}
                           </div>
                           <span className="font-bold text-gray-900">4.9/5 from 100+ reviews</span>
                        </div>
                     </div>
                  </div>
               </section>

               {/* FAQ Section */}
               <section className="py-32 px-4 bg-gradient-to-b from-gray-50 to-white">
                  <div className="max-w-4xl mx-auto">
                     <div className="text-center mb-16">
                        <div className="inline-block px-4 py-2 bg-purple-100 rounded-full mb-6">
                           <span className="text-purple-600 font-bold">💬 FAQ</span>
                        </div>
                        <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-6">Got Questions?</h2>
                        <p className="text-xl text-gray-600">We've got answers about AI features and more</p>
                     </div>

                     <div className="space-y-4">
                        {faqs.map((faq, idx) => (
                           <div
                              key={idx}
                              className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all">
                              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                                 <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                                    <span className="text-blue-600 font-bold">{idx + 1}</span>
                                 </div>
                                 {faq.q}
                              </h3>
                              <p className="text-gray-600 ml-11 leading-relaxed">{faq.a}</p>
                           </div>
                        ))}
                     </div>
                  </div>
               </section>

               {/* Final CTA - Bold & Eye-catching */}
               <section className="py-32 px-4 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 relative overflow-hidden">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.1),transparent_50%)]"></div>
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(255,255,255,0.1),transparent_50%)]"></div>

                  <div className="relative max-w-5xl mx-auto text-center text-white">
                     <div className="inline-flex items-center gap-2 px-6 py-3 bg-white/20 backdrop-blur-xl rounded-full mb-8 border border-white/30">
                        <Clock className="w-5 h-5" />
                        <span className="font-bold">Limited Time Offer</span>
                     </div>

                     <h2 className="text-5xl md:text-7xl font-black mb-8">Ready to Transform Your Business?</h2>

                     <p className="text-2xl mb-12 text-blue-100 leading-relaxed">
                        Join 50,000+ businesses already using ZeroNine AI.
                        <br />
                        Start your free trial today. No credit card required.
                     </p>

                     <div className="flex flex-col sm:flex-row gap-6 justify-center mb-12">
                        <button className="group relative px-12 py-6 rounded-2xl font-black text-xl overflow-hidden shadow-2xl hover:shadow-white/20 transition-all">
                           <div className="absolute inset-0 bg-white"></div>
                           <button onClick={() => scrollToSection('pricing')}>
                              <span className="relative bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent flex items-center justify-center gap-3">
                                 <Rocket className="w-6 h-6 text-blue-600 group-hover:rotate-12 transition-transform" />
                                 Start Free Trial
                                 <ArrowRight className="w-6 h-6 text-blue-600 group-hover:translate-x-2 transition-transform" />
                              </span>
                           </button>
                        </button>

                        <button className="px-12 py-6 rounded-2xl font-black text-xl bg-white/10 backdrop-blur-xl border-2 border-white/30 text-white hover:bg-white/20 transition-all flex items-center justify-center gap-3">
                           <Phone className="w-6 h-6" />
                           Talk to Sales
                        </button>

                        <button
                           onClick={() => setAiAssistantOpen(true)}
                           className="px-12 py-6 rounded-2xl font-black text-xl bg-green-600 text-white hover:bg-green-700 transition-all flex items-center justify-center gap-3">
                           <Bot className="w-6 h-6" />
                           AI Demo
                        </button>
                     </div>

                     <div className="flex flex-wrap justify-center gap-8 text-blue-100">
                        <div className="flex items-center gap-2">
                           <CheckCircle className="w-6 h-6" />
                           <span className="font-semibold">14-day free trial</span>
                        </div>
                        <div className="flex items-center gap-2">
                           <CheckCircle className="w-6 h-6" />
                           <span className="font-semibold">No credit card</span>
                        </div>
                        <div className="flex items-center gap-2">
                           <CheckCircle className="w-6 h-6" />
                           <span className="font-semibold">AI-powered setup</span>
                        </div>
                        <div className="flex items-center gap-2">
                           <CheckCircle className="w-6 h-6" />
                           <span className="font-semibold">Voice commands</span>
                        </div>
                     </div>
                  </div>
               </section>
            </>
            <Footer />
            {renderAIAssistant()}

            {/* AI Assistant Floating Button */}
            <button
               onClick={() => setAiAssistantOpen(true)}
               className="fixed bottom-6 right-6 w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full shadow-2xl flex items-center justify-center text-white hover:scale-110 transition-all z-40 group">
               <Bot className="w-8 h-8" />
            </button>
         </div>
      </>
   );
}
